import React, { useEffect, useRef, useState } from "react";
import "../assets/css/Courses.css";

/*
  Strategy:
  - Build slides array (original slides)
  - Create infiniteSlides = [...slides, ...slides, ...slides]
  - Start at index = slides.length (middle group)
  - Move index forward/backward; when crossing boundaries, snap to mirror index without transition
  - Compute active card styles by comparing card index to currentIndex
*/

const originalSlides = [
  {
    id: 1,
    type: "Self Paced",
    badge: "New",
    title: "Real Estate Entrepreneurship in 12 Steps Program",
    desc: "We all have a desire to own a business, either to move beyond low paying jobs..",
    duration: "240 mins",
    price: "Rs. 2,499/- (All Inclusive)",
    img: "/images/course1.jpg"
  },
  {
    id: 2,
    type: "Self Paced",
    badge: "New",
    title: "Real Estate Project Costing Program",
    desc: "Every Real Estate Project works on a budget. The success of a ...",
    duration: "50 mins",
    price: "Rs. 1,199/- (All Inclusive)",
    img: "/images/course2.jpg"
  },
  {
    id: 3,
    type: "Instructor Led",
    badge: "NEW",
    title: "DCPR – 2034 for Greater Mumbai",
    desc: "Master the intricacies of Mumbai’s DCPR 2034 for Greater Mumbai...",
    duration: "8 hrs",
    price: "Rs. 5,000/- + 18% GST",
    img: "/images/course3.jpg"
  },
  {
    id: 4,
    type: "Instructor Led",
    badge: "Best Seller",
    title: "MahaRERA Agent Training Program",
    desc: "Real Estate Agents are critical stakeholders for both promoters and allottees...",
    duration: "20 hrs",
    price: "Rs. 5,000/- + 18% GST",
    img: "/images/course4.jpg"
  }
];

const Courses = () => {
  const slides = originalSlides;
  const slidesTriple = [...slides, ...slides, ...slides]; // infinite buffer
  const base = slides.length;
  const startIndex = base; // middle copy

  const [index, setIndex] = useState(startIndex); // current index in slidesTriple
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const autoRef = useRef(null);

  // Drag state
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  // Auto-slide setup
  useEffect(() => {
    autoRef.current = () => slideTo(index + 1);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused) autoRef.current();
    }, 3800);
    return () => clearInterval(interval);
  }, [isPaused]);

  // Adjust track position whenever index changes
  useEffect(() => {
    if (!trackRef.current || !cardRef.current) return;
    const track = trackRef.current;
    const cardWidth = cardRef.current.offsetWidth;
    const gap = parseInt(getComputedStyle(track).getPropertyValue("--gap") || 24, 10);
    const offset = (cardWidth + gap) * index;
    // use transform for GPU acceleration
    setIsAnimating(true);
    track.style.transition = "transform 520ms cubic-bezier(.2,.9,.2,1)";
    track.style.transform = `translateX(${-offset}px)`;
  }, [index]);

  // When transition ends, if we're in clone zone, snap without animation
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleTransitionEnd = () => {
      setIsAnimating(false);
      const total = slidesTriple.length;
      if (index >= base * 2) {
        // jumped to clones after middle, snap back to middle equivalent
        const newIndex = index - base;
        track.style.transition = "none";
        const cardWidth = cardRef.current.offsetWidth;
        const gap = parseInt(getComputedStyle(track).getPropertyValue("--gap") || 24, 10);
        track.style.transform = `translateX(${-((cardWidth + gap) * newIndex)}px)`;
        setIndex(newIndex);
      } else if (index < base) {
        // jumped to clones before middle, snap forward
        const newIndex = index + base;
        track.style.transition = "none";
        const cardWidth = cardRef.current.offsetWidth;
        const gap = parseInt(getComputedStyle(track).getPropertyValue("--gap") || 24, 10);
        track.style.transform = `translateX(${-((cardWidth + gap) * newIndex)}px)`;
        setIndex(newIndex);
      }
    };

    track.addEventListener("transitionend", handleTransitionEnd);
    return () => track.removeEventListener("transitionend", handleTransitionEnd);
  }, [index]);

  // slideTo helper
  const slideTo = (targetIndex) => {
    if (!trackRef.current || !cardRef.current) return;
    setIndex(targetIndex);
  };

  // Prev / Next handlers
  const prev = () => {
    if (isAnimating) return;
    slideTo(index - 1);
  };
  const next = () => {
    if (isAnimating) return;
    slideTo(index + 1);
  };

  // Indicator click -> compute the closest index in the middle copy
  const goToReal = (realIdx) => {
    // choose the middle copy as anchor
    const target = base + realIdx;
    slideTo(target);
  };

  // DRAG / TOUCH Handlers
  const onPointerDown = (e) => {
    drag.current.active = true;
    drag.current.startX = e.pageX ?? e.touches?.[0]?.pageX;
    drag.current.scrollLeft = trackRef.current.style.transform
      ? -parseFloat(trackRef.current.style.transform.replace("translateX(", "").replace("px)", ""))
      : 0;
    trackRef.current.style.transition = "none";
    setIsPaused(true);
  };

  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const x = e.pageX ?? e.touches?.[0]?.pageX;
    const dx = x - drag.current.startX;
    const newOffset = drag.current.scrollLeft - dx;
    trackRef.current.style.transform = `translateX(${-newOffset}px)`;
  };

  const onPointerUp = (e) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setIsPaused(false);
    // determine nearest index by computing transform
    const transform = trackRef.current.style.transform || "";
    const match = transform.match(/translateX\((-?\d+(\.\d+)?)px\)/);
    const currentOffset = match ? Math.abs(parseFloat(match[1])) : 0;
    const cardWidth = cardRef.current.offsetWidth;
    const gap = parseInt(getComputedStyle(trackRef.current).getPropertyValue("--gap") || 24, 10);
    const per = cardWidth + gap;
    const nearest = Math.round(currentOffset / per);
    setIndex(nearest);
  };

  // Pause on hover (desktop)
  const onMouseEnter = () => setIsPaused(true);
  const onMouseLeave = () => setIsPaused(false);

  // compute active dot (map index to base slide index)
  const activeDot = ((index % base) + base) % base;

  return (
    <div className="courses-netflix-wrap" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <h2 className="courses-title">Online Programs</h2>

      <div className="courses-stage">
        <button className="courses-arrow courses-prev" onClick={prev} aria-label="Previous">‹</button>

        <div
          className="courses-viewport"
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        >
          <div className="courses-track" ref={trackRef} style={{ "--gap": 24 }}>
            {slidesTriple.map((s, i) => {
              // decide visual position relative to current index
              const relative = i - index;
              // normalize for index around center (for nice effect when clones are used)
              // we will limit transform intensity for large relative values
              let transformClass = "";
              if (i === index) transformClass = "center";
              else if (i === index - 1) transformClass = "left";
              else if (i === index + 1) transformClass = "right";
              else if (Math.abs(relative) <= 3) transformClass = relative < 0 ? "left-far" : "right-far";
              else transformClass = "far";

              return (
                <div
                  key={i + "-" + s.id}
                  className={`courses-card ${transformClass}`}
                  ref={i === index ? cardRef : null}
                >
                  <img className="courses-img" src={s.img} alt={s.title} />
                  <div className="courses-meta">
                    <div className="courses-top">
                      <span className="courses-type">{s.type}</span>
                      <span className="courses-badge">{s.badge}</span>
                    </div>
                    <h3 className="courses-h">{s.title}</h3>
                    <p className="courses-desc">{s.desc}</p>
                    <div className="courses-bottom">
                      <span className="courses-duration">⏱ {s.duration}</span>
                      <span className="courses-price">{s.price}</span>
                    </div>
                    <button className="courses-cta">Learn More</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button className="courses-arrow courses-next" onClick={next} aria-label="Next">›</button>
      </div>

      {/* indicators */}
      <div className="courses-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`courses-dot ${i === activeDot ? "active" : ""}`}
            onClick={() => goToReal(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Courses;
